import { useSessionStore } from '../api/session';
import type { TenantScope } from './tenant-db';

/**
 * Wat de offline laag uit de sessie leest - en niets meer:
 *
 * - `session.tenant.uuid`: de actieve omgeving, en dus welke database open is;
 * - `session.user.uuid`: van wie die database is;
 * - `session.device.uuid`: de `device_uuid` van een push.
 *
 * De sessie zelf is van `shared/api/session.ts`. De lezers hieronder zijn
 * structureel, zodat deze laag niet meebeweegt met velden die hij niet nodig
 * heeft.
 */

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function uuidOf(value: unknown): string | null {
    return isRecord(value) && typeof value.uuid === 'string' && value.uuid !== ''
        ? value.uuid
        : null;
}

export function tenantUuidOf(session: unknown): string | null {
    return isRecord(session) ? uuidOf(session.tenant) : null;
}

export function userUuidOf(session: unknown): string | null {
    return isRecord(session) ? uuidOf(session.user) : null;
}

export function deviceUuidOf(session: unknown): string | null {
    return isRecord(session) ? uuidOf(session.device) : null;
}

/**
 * De omgeving waarvan de gegevens op dit apparaat open staan. Tijdens het
 * meekijken als iemand anders is dat bewust geen enkele: een platformbeheerder
 * mag niet in de outbox van een inspecteur schrijven of zijn dossiers
 * binnenhalen.
 */
export function tenantScopeOf(session: unknown, impersonating: boolean): TenantScope | null {
    const tenantUuid = tenantUuidOf(session);

    if (impersonating || !tenantUuid) {
        return null;
    }

    return { tenantUuid, userUuid: userUuidOf(session) };
}

/** Een stabiele sleutel voor de scope, handig als React-afhankelijkheid. */
export function tenantScopeKey(scope: TenantScope | null): string | null {
    return scope ? `${scope.tenantUuid}:${scope.userUuid ?? ''}` : null;
}

export function currentDeviceUuid(): string | null {
    return deviceUuidOf(useSessionStore.getState().session);
}
